import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CarFront, Search, Fuel, Gauge, Calendar, Banknote, RefreshCw, ExternalLink, SlidersHorizontal } from "lucide-react";
import { schemaFaq, combineSchemas, schemaService } from "@/lib/siteSchemas";

const NLT_FAQS = [
  {
    q: "Quanto costa il noleggio lungo termine in Campania?",
    a: "Le rate Ayvens partono da circa 250-300€/mese per utilitarie come Fiat 500 o Lancia Y, fino a 800-1500€/mese per SUV premium. Il prezzo include bollo, RC Auto, manutenzione ordinaria e assistenza stradale. La rata effettiva dipende da chilometri annui e durata (24-60 mesi).",
  },
  {
    q: "Cosa è incluso nel canone NLT Ayvens?",
    a: "Bollo auto, copertura RC Auto, kasko, furto/incendio, manutenzione ordinaria e straordinaria, soccorso stradale 24/7, auto sostitutiva. Non incluso: carburante, multe, danni fuori franchigia.",
  },
  {
    q: "Posso fare il noleggio lungo termine come privato?",
    a: "Sì. Ayvens accetta clienti privati con busta paga, pensionati e liberi professionisti. La pratica si chiude in 5-7 giorni lavorativi previa istruttoria creditizia. Colucci Assicuratori segue tutto il processo end-to-end nelle 3 sedi Campania.",
  },
  {
    q: "Differenza tra noleggio lungo termine e leasing?",
    a: "Nel NLT paghi un canone all-inclusive per usare l'auto: niente acquisto finale, niente preoccupazioni manutenzione/bollo. Nel leasing finanzi l'acquisto e a fine contratto puoi riscattarla. Il NLT conviene se vuoi cambiare auto ogni 2-3 anni senza pensieri.",
  },
  {
    q: "Quanti chilometri annui posso fare?",
    a: "I pacchetti standard partono da 10.000 km/anno fino a 40.000+ km/anno. Più alto il chilometraggio, più alto il canone. Si può modificare il pacchetto durante il contratto se i tuoi km cambiano.",
  },
  {
    q: "Posso ritirare l'auto a Napoli, Portici o Pomigliano?",
    a: "Sì. Colucci Assicuratori ha 3 sedi (Portici Via Diaz, Napoli Via G. Bruno, Pomigliano Via Carducci) dove ti seguiamo dalla scelta auto al ritiro. La consegna fisica avviene in concessionaria convenzionata Ayvens secondo disponibilità.",
  },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_INTERVAL = 15000;

const FUEL_LABELS = {
  PETROL: "Benzina",
  PETROL_MILD_HYBRID: "Ibrida Mild",
  PETROL_FULL_HYBRID: "Ibrida Full",
  DIESEL: "Diesel",
  ELECTRIC: "Elettrica",
  LPG: "GPL",
  CNG: "Metano",
  PLUG_IN_HYBRID: "Plug-in Hybrid",
};

const TRANS_LABELS = {
  MAN5: "Manuale 5",
  MAN6: "Manuale 6",
  AUT: "Automatico",
  EDCT: "Automatico DCT",
};

function fmt(val) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

export default function NoleggoPage() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [search, setSearch] = useState("");
  const [fuelFilter, setFuelFilter] = useState("all");
  const [transFilter, setTransFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [maxPrice, setMaxPrice] = useState(2000);
  const [quickFilter, setQuickFilter] = useState(""); // "green" | "auto" | "budget" | ""
  const [sortBy, setSortBy] = useState("price_asc");
  const [target, setTarget] = useState("privati");
  const [source, setSource] = useState("all"); // "all" | "ayvens" | "arval" (FASE 4.2.8)
  const [sourceCounts, setSourceCounts] = useState({ arval: 0, ayvens: 0 });
  const [tipoCounts, setTipoCounts] = useState({ all: 0, privati: 0, business: 0 });

  // Fetch: applica i filtri tipo_cliente + source via backend (cross-availability
  // rule per Ayvens business → Privati). Anche un fetch supplementare per i
  // contatori del segmento opposto e per il provider non selezionato.
  const fetchCars = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("tipo_cliente", target);
      if (source !== "all") params.append("source", source);
      params.append("limit", "2000");
      const resp = await fetch(`${API}/noleggio/catalog?${params.toString()}`);
      const data = await resp.json();
      setCars(Array.isArray(data?.cars) ? data.cars : []);
      setSourceCounts(data?.sources || { arval: 0, ayvens: 0 });
      setLastUpdate(new Date());

      // Contatori indipendenti per i chip (sempre stesso tipo_cliente, source variato)
      const [allRes, ayvRes, arvRes] = await Promise.all([
        fetch(`${API}/noleggio/catalog?tipo_cliente=${target}&limit=2000`).then(r => r.json()).catch(() => ({})),
        fetch(`${API}/noleggio/catalog?tipo_cliente=${target}&source=ayvens&limit=2000`).then(r => r.json()).catch(() => ({})),
        fetch(`${API}/noleggio/catalog?tipo_cliente=${target}&source=arval&limit=2000`).then(r => r.json()).catch(() => ({})),
      ]);
      setTipoCounts({
        all: allRes?.total || 0,
        ayvens: ayvRes?.total || 0,
        arval: arvRes?.total || 0,
      });
    } catch (err) {
      console.error("Errore fetch auto:", err);
    } finally {
      setLoading(false);
    }
  }, [target, source]);

  useEffect(() => {
    fetchCars();
    const interval = setInterval(fetchCars, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCars]);

  // Quando le auto caricano la prima volta, imposta maxPrice al prezzo massimo reale
  useEffect(() => {
    if (cars.length > 0 && maxPrice === 2000) {
      const cap = Math.max(800, ...cars.map(c => c.monthlyFee || 0));
      setMaxPrice(cap);
    }
  }, [cars]); // eslint-disable-line react-hooks/exhaustive-deps

  const fuelTypes = [...new Set(cars.map(c => c.fuelType))].sort();
  const transTypes = [...new Set(cars.map(c => c.transmission).filter(Boolean))].sort();
  const brands = [...new Set(cars.map(c => c.brand).filter(Boolean))].sort();
  const priceCap = Math.max(800, ...cars.map(c => c.monthlyFee || 0));

  const resetFilters = () => {
    setSearch(""); setFuelFilter("all"); setTransFilter("all"); setBrandFilter("all");
    setMaxPrice(priceCap); setQuickFilter(""); setSortBy("price_asc");
  };

  const isGreen = (c) => ["ELECTRIC", "PLUG_IN_HYBRID", "PETROL_FULL_HYBRID"].includes(c.fuelType);

  // Comparator cross-provider: indexa tutti i veicoli per chiave brand+model
  // normalizzata. Così ogni card può mostrare "anche su {altro provider} da €X".
  const normalizeKey = (c) => {
    const b = (c.brand || "").toLowerCase().trim();
    const m = (c.model || "").toLowerCase().replace(/[\s']+/g, "").replace(/[.,]/g, "");
    return `${b}|${m}`;
  };
  const crossPriceMap = (() => {
    const map = {};
    for (const c of cars) {
      const k = normalizeKey(c);
      if (!map[k]) map[k] = {};
      if (!map[k][c.source] || (c.monthlyFee || 99999) < (map[k][c.source].monthlyFee || 99999)) {
        map[k][c.source] = c;
      }
    }
    return map;
  })();

  const filtered = cars
    .filter(c => {
      if (source !== "all" && c.source !== source) return false;
      const q = search.toLowerCase();
      if (q && !`${c.brand} ${c.model} ${c.modelDetails}`.toLowerCase().includes(q)) return false;
      if (fuelFilter !== "all" && c.fuelType !== fuelFilter) return false;
      if (transFilter !== "all" && c.transmission !== transFilter) return false;
      if (brandFilter !== "all" && c.brand !== brandFilter) return false;
      if ((c.monthlyFee || 0) > maxPrice) return false;
      if (quickFilter === "green" && !isGreen(c)) return false;
      if (quickFilter === "auto" && !["AUT", "EDCT"].includes(c.transmission) && (c.transmission || "").toLowerCase() !== "automatico") return false;
      if (quickFilter === "budget" && (c.monthlyFee || 0) > 400) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "price_asc") return (a.monthlyFee || 99999) - (b.monthlyFee || 99999);
      if (sortBy === "price_desc") return (b.monthlyFee || 0) - (a.monthlyFee || 0);
      if (sortBy === "brand") return a.brand.localeCompare(b.brand);
      if (sortBy === "availability") {
        const order = { "pronta_consegna": 0, "30gg": 1, "su_ordinazione": 2 };
        return (order[a.disponibilita] ?? 3) - (order[b.disponibilita] ?? 3);
      }
      if (sortBy === "provider") return (a.source || "").localeCompare(b.source || "");
      return 0;
    });

  const activeFiltersCount =
    (search ? 1 : 0) + (fuelFilter !== "all" ? 1 : 0) + (transFilter !== "all" ? 1 : 0)
    + (brandFilter !== "all" ? 1 : 0) + (maxPrice < priceCap ? 1 : 0) + (quickFilter ? 1 : 0);

  return (
    <>
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(combineSchemas(
            schemaService({
              name: "Noleggio Lungo Termine Auto",
              description: "Noleggio lungo termine Ayvens con assicurazione, bollo e manutenzione inclusi. Per privati, professionisti, partite IVA e aziende in Campania.",
              slug: "noleggio",
              serviceType: "AutomotiveService",
            }),
            schemaFaq(NLT_FAQS),
          )),
        }}
      />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="noleggio-page">
        {/* Hero — Branding Colucci Solutions + BlueAssistance */}
        <section className="relative bg-gradient-to-br from-[#0F1F14] via-[#163321] to-[#1d4a2e] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px"}} />
          <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#D97B58]/10 blur-3xl" />
          <div className="relative max-w-6xl mx-auto px-5 sm:px-6 py-12 sm:py-16">
            <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-[10px] font-semibold uppercase tracking-wider text-[#D97B58] mb-4">
                  <CarFront className="w-3 h-3" /> Noleggio a Lungo Termine
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-testid="noleggio-title">
                  Mobilità <span className="text-[#D97B58]">su misura</span>
                </h1>
                <p className="text-base text-white/80 max-w-xl leading-relaxed mb-4">
                  Intermediari certificati <strong className="text-white">BlueAssistance</strong>, in partnership con <strong className="text-white">Ayvens</strong> e <strong className="text-white">Arval</strong>. Scopri tutte le offerte NLT selezionate per privati e aziende.
                </p>
                <div className="flex items-center gap-2 text-[10px] text-white/50">
                  <RefreshCw className="w-3 h-3" />
                  <span>Aggiornamento automatico ogni ora</span>
                  {lastUpdate && <span className="hidden sm:inline">· Ultimo: {lastUpdate.toLocaleTimeString("it-IT")}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-3">
                <img src="/colucci_solutions_web.png" alt="Colucci Solutions" className="h-12 sm:h-14 object-contain brightness-0 invert" data-testid="noleggio-logo" />
                <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-white/60 font-semibold flex-wrap justify-end">
                  <span className="px-2 py-1 bg-white/5 border border-white/10 rounded-md">BlueAssistance</span>
                  <span className="px-2 py-1 bg-white/5 border border-white/10 rounded-md" data-testid="noleggio-partner-ayvens">Ayvens</span>
                  <span className="px-2 py-1 bg-white/5 border border-white/10 rounded-md" data-testid="noleggio-partner-arval">Arval</span>
                </div>
              </div>
            </div>

            {/* Quick stats + CTA Novità */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 border-t border-white/10">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Veicoli disponibili</p>
                <p className="font-heading text-2xl font-bold">{cars.length}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Partner NLT</p>
                <p className="font-heading text-base font-bold leading-tight">Ayvens<br/><span className="text-[#D97B58]">+ Arval</span></p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Consulenza</p>
                <p className="font-heading text-sm font-bold text-[#D97B58]">Gratuita</p>
              </div>
              <Link to="/noleggio/novita" className="group bg-[#D97B58]/20 hover:bg-[#D97B58]/30 border border-[#D97B58]/40 rounded-xl px-4 py-2.5 transition-colors flex items-center justify-between" data-testid="nav-novita-noleggio">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#D97B58] mb-0.5 font-bold">Novità NLT</p>
                  <p className="text-xs text-white">Rubriche e nuove auto →</p>
                </div>
              </Link>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8">
          {/* 3-way Provider toggle: Tutte / Ayvens / Arval (FASE 4.2.8) */}
          <div className="flex items-center justify-center gap-3 mb-3 flex-wrap" data-testid="noleggio-source-toggle">
            <div className="inline-flex bg-white rounded-full border border-[#E5E2DB] p-1 shadow-sm" role="tablist" aria-label="Filtra per partner NLT">
              {[
                { key: "all", label: "Tutte", count: tipoCounts.all, testid: "noleggio-source-all" },
                { key: "ayvens", label: "Ayvens", count: tipoCounts.ayvens, testid: "noleggio-source-ayvens" },
                { key: "arval", label: "Arval", count: tipoCounts.arval, testid: "noleggio-source-arval" },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSource(opt.key)}
                  className={`px-5 py-2 rounded-full text-xs font-semibold transition-all ${
                    source === opt.key ? "bg-[#163321] text-white" : "text-[#4A5E51] hover:text-[#163321]"
                  }`}
                  data-testid={opt.testid}
                  role="tab"
                  aria-selected={source === opt.key}
                >
                  {opt.label} <span className="opacity-80 tabular-nums">({opt.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tipo cliente Privati/Business + tooltip cross-availability */}
          <div className="flex items-center justify-center gap-3 mb-3 flex-wrap" data-testid="noleggio-target-toggle">
            <div className="inline-flex bg-white rounded-full border border-[#E5E2DB] p-1 shadow-sm">
              <button
                onClick={() => setTarget("privati")}
                className={`px-5 py-2 rounded-full text-xs font-semibold transition-all ${
                  target === "privati" ? "bg-[#163321] text-white" : "text-[#4A5E51] hover:text-[#163321]"
                }`}
                data-testid="noleggio-target-privati"
              >
                Privati
              </button>
              <button
                onClick={() => setTarget("business")}
                className={`px-5 py-2 rounded-full text-xs font-semibold transition-all ${
                  target === "business" ? "bg-[#163321] text-white" : "text-[#4A5E51] hover:text-[#163321]"
                }`}
                data-testid="noleggio-target-business"
              >
                Business / P.IVA
              </button>
            </div>
            {target === "privati" && (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-[#4A5E51] cursor-help"
                title="I veicoli business di Ayvens sono noleggiabili anche da privati: te li includiamo nel catalogo Privati."
                data-testid="noleggio-cross-availability-tooltip"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-[#D97B58]/20 text-[#D97B58] font-bold">ⓘ</span>
                Cross-availability Ayvens
              </span>
            )}
          </div>
          <p className="text-center text-[11px] text-[#4A5E51] mb-6 max-w-2xl mx-auto" data-testid="noleggio-target-hint">
            {target === "privati" ? (
              <>
                Offerte <strong>Privati</strong>: canone listino <strong>IVA esclusa</strong>, pacchetto a <strong>km totali</strong> sul contratto (es. 120.000 km su 48 mesi).
              </>
            ) : (
              <>
                Offerte <strong>Business / P.IVA</strong>: canone listino <strong>IVA esclusa</strong>, pacchetto a <strong>km/anno</strong> standard NLT (10.000-40.000 km/anno).
                L'IVA al 22% &egrave; <strong>detraibile</strong> per ditte individuali, partite IVA e aziende.
              </>
            )}
          </p>

          {/* Quick filters pills (ecommerce-like) */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="noleggio-quick-filters">
            {[
              { k: "", label: "Tutti", emoji: null },
              { k: "green", label: "Green (Elettrico / Ibrido)", emoji: "leaf" },
              { k: "auto", label: "Cambio automatico", emoji: "auto" },
              { k: "budget", label: "Sotto 400 EUR/mese", emoji: "budget" },
            ].map(opt => (
              <button
                key={opt.k}
                onClick={() => setQuickFilter(opt.k)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
                  quickFilter === opt.k
                    ? "bg-[#163321] text-white border-[#163321] shadow-sm"
                    : "bg-white text-[#4A5E51] border-[#E5E2DB] hover:border-[#163321]/40"
                }`}
                data-testid={`quick-filter-${opt.k || "all"}`}>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Filtri avanzati */}
          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-4 sm:p-5 mb-6 shadow-sm" data-testid="noleggio-filters">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <SlidersHorizontal className="w-4 h-4 text-[#4A5E51]" />
              <span className="text-xs font-medium text-[#4A5E51] uppercase tracking-wider">Filtra e ordina</span>
              {activeFiltersCount > 0 && (
                <span className="text-[10px] font-bold text-white bg-[#D97B58] px-2 py-0.5 rounded-full">
                  {activeFiltersCount} attivi
                </span>
              )}
              <span className="ml-auto text-xs text-[#4A5E51]">{filtered.length} veicoli</span>
              {activeFiltersCount > 0 && (
                <button onClick={resetFilters} className="text-xs text-[#D97B58] font-semibold hover:underline" data-testid="reset-filters-btn">
                  Azzera
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative lg:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Cerca marca o modello..."
                  className="rounded-xl border-[#E5E2DB] h-10 pl-10 text-sm"
                  data-testid="noleggio-search"
                />
              </div>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="rounded-xl border-[#E5E2DB] h-10 text-sm" data-testid="noleggio-brand-filter">
                  <SelectValue placeholder="Marca" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le marche</SelectItem>
                  {brands.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="rounded-xl border-[#E5E2DB] h-10 text-sm" data-testid="noleggio-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_asc">Prezzo crescente</SelectItem>
                  <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
                  <SelectItem value="brand">Marca A-Z</SelectItem>
                  <SelectItem value="availability">Disponibilità</SelectItem>
                  <SelectItem value="provider">Provider</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fuelFilter} onValueChange={setFuelFilter}>
                <SelectTrigger className="rounded-xl border-[#E5E2DB] h-10 text-sm" data-testid="noleggio-fuel-filter">
                  <SelectValue placeholder="Alimentazione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le alimentazioni</SelectItem>
                  {fuelTypes.map(f => (
                    <SelectItem key={f} value={f}>{FUEL_LABELS[f] || f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={transFilter} onValueChange={setTransFilter}>
                <SelectTrigger className="rounded-xl border-[#E5E2DB] h-10 text-sm" data-testid="noleggio-trans-filter">
                  <SelectValue placeholder="Trasmissione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le trasmissioni</SelectItem>
                  {transTypes.map(t => (
                    <SelectItem key={t} value={t}>{TRANS_LABELS[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Price slider */}
              <div className="lg:col-span-2 flex items-center gap-3 px-2">
                <Banknote className="w-4 h-4 text-[#4A5E51] shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between text-[11px] text-[#4A5E51] mb-1">
                    <span>Prezzo max</span>
                    <span className="font-semibold text-[#0F1F14]">{fmt(maxPrice)}/mese</span>
                  </div>
                  <input
                    type="range"
                    min="200"
                    max={priceCap}
                    step="50"
                    value={maxPrice}
                    onChange={e => setMaxPrice(Number(e.target.value))}
                    className="w-full accent-[#163321] h-1.5"
                    data-testid="price-slider"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-6 h-6 text-[#163321] animate-spin" />
              <span className="ml-3 text-sm text-[#4A5E51]">Caricamento offerte...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <CarFront className="w-12 h-12 text-[#E5E2DB] mx-auto mb-3" />
              <p className="text-sm text-[#4A5E51]">Nessun veicolo trovato con i filtri selezionati</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="noleggio-grid">
              {filtered.map(car => (
                <CarCard key={car.id} car={car} crossPriceMap={crossPriceMap} normalizeKey={normalizeKey} />
              ))}
            </div>
          )}

          {/* Vantaggi */}
          <div className="mt-12 p-6 sm:p-8 bg-[#F5F4F0] rounded-2xl">
            <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-4 text-center">Cosa include il canone</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                "Assicurazione RCA", "Manutenzione ordinaria e straordinaria", "Soccorso stradale", "Gestione multe",
                "Bollo auto", "Tutela legale", "Immatricolazione", "Assistenza dedicata",
              ].map(v => (
                <div key={v} className="flex items-center gap-2 text-xs text-[#4A5E51]">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#163321] shrink-0" />
                  {v}
                </div>
              ))}
            </div>
          </div>

          {/* Penalità, franchigie e costi accessori — riepilogo da contratto Ayvens */}
          <div className="mt-6 bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm" data-testid="noleggio-penali-riepilogo">
            <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-1">Cosa NON è incluso nel canone (da contratto Ayvens)</h2>
            <p className="text-xs text-[#4A5E51] mb-5">
              Valori dai documenti contrattuali ufficiali Ayvens (Informazioni Precontrattuali + Limitazione Responsabilità Danni 2025-2026).
              Importi specifici di ogni veicolo nella <strong>Lettera di Offerta</strong> firmata.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { label: "Costo km eccedenti", value: "Da Lettera di Offerta", note: "Importo €/km contrattuale, conguaglio a fine contratto" },
                { label: "Km non utilizzati", value: "Non rimborsati", note: "Standard NLT: i km in difetto non danno rimborso" },
                { label: "Quota responsabilità danni", value: "Es. 249 € / 500 €", note: "Negoziabile in Lettera di Offerta. Possibile zero franchigia" },
                { label: "Recesso entro 14 giorni", value: "3 mensilità", note: "Recesso pre-immatricolazione, esente IVA" },
                { label: "Recesso post-immatricolazione", value: "4 mensilità", note: "Tra immatricolazione e MAD del veicolo" },
                { label: "Mancato ritiro post-MAD", value: "6 mensilità", note: "Recesso dopo MAD o mancato ritiro entro 15gg" },
                { label: "Apertura pratica", value: "150,00 € + IVA", note: "Una tantum alla conclusione del primo contratto" },
                { label: "Sinistri passivi (dal 4°)", value: "250,00 € cad.", note: "Penale per ogni sinistro dal 4° al 6°. 7° = risoluzione" },
                { label: "Insolvenza c/c", value: "20,00 €", note: "Esente IVA art. 15 DPR 633/72" },
              ].map(p => (
                <div key={p.label} className="p-3 bg-[#F5F4F0] rounded-xl">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <p className="text-xs font-medium text-[#0F1F14]">{p.label}</p>
                    <p className="text-xs font-bold text-[#163321] tabular-nums whitespace-nowrap">{p.value}</p>
                  </div>
                  <p className="text-[10px] text-[#4A5E51] leading-relaxed">{p.note}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-[#FFF6EE] rounded-xl text-[11px] text-[#7A4A2E]">
              <strong>Nota:</strong> tutte le franchigie sono negoziabili in Lettera di Offerta (esempi documentati €249/€500). Le penali
              di recesso sono <strong>esenti IVA art. 15 DPR 633/72</strong>. La Limitazione di Responsabilità non copre dolo, danni interni,
              pneumatici, riparazioni arbitrarie e danni non denunciati prima della riconsegna.
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <p className="text-xs text-[#4A5E51] mb-3">Offerte da <strong>Ayvens</strong>, leader europeo del noleggio a lungo termine</p>
            <div className="inline-flex items-center gap-4 text-xs text-[#163321]">
              <a href="https://noleggio.ayvens.com/it-it/" target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1" data-testid="noleggio-ayvens-link">
                <ExternalLink className="w-3 h-3" /> Vai al sito Ayvens
              </a>
            </div>
            <p className="text-[10px] text-[#4A5E51]/70 mt-4 italic max-w-2xl mx-auto" data-testid="copyright-disclaimer">
              Le immagini dei veicoli sono rielaborate per uso editoriale e commerciale nel rispetto del copyright dei partner.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function CarCard({ car, crossPriceMap, normalizeKey }) {
  const deposit = car.deposit ?? car.advancedPayment ?? 0;
  const duration = car.durationMonths ?? car.duration ?? 48;
  const kmPerYear = car.kmPerYear ?? (car.kmTotal ? Math.round(car.kmTotal / (duration / 12)) : (car.mileage ? Math.round(car.mileage / (duration / 12)) : 10000));
  const isPrivati = car.target === "privati";
  // Su offerte Privati Ayvens espone "Km totali"; mostriamo entrambi per chiarezza.
  // Su offerte Business mostriamo solo i km/anno (formato standard NLT P.IVA).
  const kmLabel = isPrivati && car.kmTotal
    ? `${(car.kmTotal / 1000).toFixed(0)}.000 km totali`
    : `${(kmPerYear / 1000).toFixed(0)}.000 km/anno`;
  const vatLabel = car.vatIncluded ? "IVA inclusa" : "IVA esclusa";
  const rataZeroAnticipo = deposit > 0
    ? Math.ceil(car.monthlyFee + deposit / duration)
    : car.monthlyFee;
  const imgSrcOriginal = (car.imageUrl || "").startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${car.imageUrl}`
    : car.imageUrl;
  // FASE 4.2.11.1 — endpoint ottimizzato (smart-crop + WebP 400x300, ~10KB)
  // NB: NON usa AI rework qui (troppo lento/costoso per 25+ card simultanee).
  //     L'AI rework viene applicato solo nella pagina di dettaglio.
  const imgSrc = imgSrcOriginal
    ? `${process.env.REACT_APP_BACKEND_URL}/api/noleggio/images/optimized?src=${encodeURIComponent(imgSrcOriginal)}&size=card`
    : imgSrcOriginal;
  const imgSrcDetail = imgSrcOriginal
    ? `${process.env.REACT_APP_BACKEND_URL}/api/noleggio/images/optimized?src=${encodeURIComponent(imgSrcOriginal)}&size=detail`
    : imgSrcOriginal;

  // Confronto cross-provider
  const otherSource = car.source === "arval" ? "ayvens" : "arval";
  const otherSourceLabel = otherSource === "arval" ? "Arval" : "Ayvens";
  const compareCar = (crossPriceMap && normalizeKey)
    ? (crossPriceMap[normalizeKey(car)] || {})[otherSource]
    : null;
  const priceDiff = compareCar ? (car.monthlyFee - compareCar.monthlyFee) : 0;
  const isCheaperHere = priceDiff < 0;

  return (
    <div
      className="bg-white rounded-2xl border border-[#E5E2DB] overflow-hidden shadow-sm hover:shadow-md hover:border-[#163321]/20 transition-all group"
      data-testid={`car-card-${car.id}`}
    >
      {/* Image — v1.14.47: ridotto padding e altezza immagine massima per
          riempire correttamente l'area card. Sfondo neutro coerente. */}
      <div className="relative bg-[#F5F4F0] flex items-center justify-center aspect-[4/3] overflow-hidden">
        <span className={`absolute top-3 left-3 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
          car.source === "ayvens" ? "bg-[#6B4E8A]" : "bg-[#D97B58]"
        }`}>
          {car.source === "ayvens" ? "Ayvens" : "Arval"}
        </span>
        <span
          className={`absolute top-3 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
            isPrivati
              ? "bg-white text-[#163321] border-[#163321]/30"
              : "bg-[#163321] text-white border-[#163321]"
          }`}
          data-testid={`car-target-${car.id}`}
        >
          {isPrivati ? "Privati" : "Business / P.IVA"}
        </span>
        <img
          src={imgSrc}
          srcSet={imgSrcDetail ? `${imgSrc} 400w, ${imgSrcDetail} 800w` : undefined}
          sizes="(max-width: 640px) 400px, 400px"
          alt={`${car.brand} ${car.model}`}
          className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform"
          loading="lazy"
          decoding="async"
          width="400"
          height="300"
          onError={(e) => {
            // Fallback 1: usa URL originale se l'AI proxy fallisce
            if (imgSrcOriginal && e.currentTarget.src !== imgSrcOriginal) {
              e.currentTarget.src = imgSrcOriginal;
              return;
            }
            e.currentTarget.style.display = "none";
            const fallback = e.currentTarget.nextElementSibling;
            if (fallback) fallback.style.display = "flex";
          }}
        />
        <div className="absolute inset-0 hidden items-center justify-center text-[#4A5E51] text-xs">
          <div className="text-center">
            <CarFront className="w-8 h-8 mx-auto mb-1 opacity-30" />
            <p className="font-medium">{car.brand}</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 space-y-3">
        <div>
          <p className="text-[10px] text-[#4A5E51] uppercase tracking-wider">Noleggio Lungo Termine</p>
          <h3 className="font-heading text-base font-medium text-[#0F1F14]">{car.brand} {car.model}</h3>
          <p className="text-[10px] text-[#4A5E51] leading-snug mt-0.5 line-clamp-2">{car.modelDetails}</p>
        </div>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-2">
          <Spec icon={Fuel} label={FUEL_LABELS[car.fuelType] || car.fuelType} />
          <Spec icon={Gauge} label={TRANS_LABELS[car.transmission] || car.transmission} />
          <Spec icon={Calendar} label={`${duration} mesi`} />
          <Spec icon={CarFront} label={kmLabel} />
        </div>
        {isPrivati && car.kmTotal && (
          <p className="text-[10px] text-[#4A5E51] -mt-1">
            Equivalente {(kmPerYear / 1000).toFixed(0)}.000 km/anno su {duration} mesi
          </p>
        )}

        {/* Prezzo */}
        <div className="pt-2 border-t border-[#E5E2DB] space-y-1.5">
          {deposit > 0 ? (
            <>
              <div className="flex items-end justify-between">
                <p className="text-[10px] text-[#4A5E51]">Con anticipo {fmt(deposit)}</p>
                <div className="text-right">
                  <p className="text-lg font-heading font-medium text-[#163321]">{fmt(car.monthlyFee)}<span className="text-[10px] font-normal text-[#4A5E51]">/mese</span></p>
                  <p className="text-[9px] text-[#4A5E51]">{vatLabel}</p>
                </div>
              </div>
              <div className="flex items-end justify-between bg-[#163321]/5 rounded-lg px-3 py-2">
                <p className="text-[10px] text-[#163321] font-medium">Zero anticipo</p>
                <div className="text-right">
                  <p className="text-lg font-heading font-medium text-[#163321]">{fmt(rataZeroAnticipo)}<span className="text-[10px] font-normal text-[#4A5E51]">/mese</span></p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-end justify-between">
              <p className="text-[10px] text-[#163321] font-medium">Zero anticipo</p>
              <div className="text-right">
                <p className="text-xl font-heading font-medium text-[#163321]">{fmt(car.monthlyFee)}</p>
                <p className="text-[10px] text-[#4A5E51]">al mese {vatLabel}</p>
              </div>
            </div>
          )}
        </div>

        {/* Cross-provider price compare */}
        {compareCar && (
          <div
            className={`rounded-xl px-3 py-2 border text-[10.5px] flex items-center justify-between gap-2 ${
              isCheaperHere
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-amber-50 border-amber-200 text-amber-800"
            }`}
            data-testid={`compare-${car.id}`}
          >
            <span className="font-medium">
              {isCheaperHere
                ? `Risparmi ${fmt(Math.abs(priceDiff))}/mese vs ${otherSourceLabel}`
                : `Su ${otherSourceLabel} da ${fmt(compareCar.monthlyFee)}/mese`}
            </span>
            <span className="text-[9px] opacity-60 whitespace-nowrap">confronto {otherSourceLabel}</span>
          </div>
        )}

        {/* CTA */}
        <a
          href={`/noleggio/${car.id}`}
          className="block w-full text-center py-2.5 rounded-xl bg-[#163321] text-white text-sm font-medium hover:bg-[#224A32] transition-colors"
          data-testid={`car-cta-${car.id}`}
        >
          Scopri di pi&ugrave;
        </a>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-[#4A5E51]">
      <Icon className="w-3 h-3 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}
